/* Collection landing headers (sections/kiids-landing-hero.liquid): behaviour of the three scenes.
   mobile = Detská izba, a crib mobile of pendulums; track = Radosť z jazdy, a floating vehicle sticker per age; board = Radosť z hry, a ball pit.
   Levels follow window.kiidsMotion (kiids-motion.js): full = everything, gentle = clicks only, none / "Menej pohybu" / reduced motion = static end state. */
(() => {
  const NS = 'http://www.w3.org/2000/svg';
  const mo = () => window.kiidsMotion;
  const on = (fx) => !!mo() && mo().on(fx);
  const paused = () => !!mo() && mo().paused();
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const rand = (a, b) => a + Math.random() * (b - a);
  const COLORS = ['#C6FF3D', '#FFF7E8', '#5c02ae', '#ffffff'];

  // Six tiny stars flying out of (x, y) inside host (a positioned element).
  const spark = (host, x, y) => {
    if (!on('fly') || !host.animate) return;
    for (let i = 0; i < 5; i++) {
      const s = document.createElement('i');
      s.className = 'kiids-lh__spark';
      s.setAttribute('aria-hidden', 'true');
      s.style.cssText = `left:${x}px;top:${y}px;background:${COLORS[i % 4]}`;
      host.appendChild(s);
      const a = (i / 5) * Math.PI * 2 + rand(0, .5), d = rand(34, 66);
      s.animate([
        { transform: 'translate(-50%, -50%) scale(.4) rotate(0deg)', opacity: 1 },
        { transform: `translate(calc(-50% + ${Math.cos(a) * d}px), calc(-50% + ${Math.sin(a) * d}px)) scale(1.1) rotate(${rand(120, 300)}deg)`, opacity: 0 }
      ], { duration: rand(650, 900), easing: 'cubic-bezier(.2, .7, .3, 1)' }).onfinish = () => s.remove();
    }
  };

  // ---- Detská izba: every ball is a damped pendulum with a breeze; the mouse, a touch scroll or a tap pushes it, neighbours follow ----
  const crib = (root) => {
    const items = [...root.querySelectorAll('.kiids-lh__item')];
    const links = items.map((li) => li.querySelector('.kiids-lh__link'));
    const st = items.map((_, i) => ({ a: 0, v: 0, ph: i * 0.9, sparked: 0 }));
    let raf = 0, last = 0;

    const push = (i, dv) => {
      [[i, 1], [i - 1, .35], [i + 1, .35]].forEach(([k, f]) => { if (st[k]) st[k].v = clamp(st[k].v + dv * f, -90, 90); });
      wake();
    };
    const tick = (t) => {
      if (!on('tilt') || paused()) { raf = 0; return; }
      const dt = Math.min(.05, (t - last) / 1000 || .016);
      last = t;
      st.forEach((s, i) => {
        const rest = 2.2 * Math.sin(t / 1000 * .7 + s.ph); // breeze: the spring follows a slowly moving rest angle
        s.v += (-8 * (s.a - rest) - 1.7 * s.v) * dt;
        s.a += s.v * dt;
        links[i].style.transform = `rotate(${s.a.toFixed(2)}deg)`;
      });
      raf = requestAnimationFrame(tick);
    };
    function wake() {
      if (!on('tilt') || paused()) return;
      root.classList.add('is-phys');
      if (!raf) { last = performance.now(); raf = requestAnimationFrame(tick); }
    }
    const sleep = () => {
      cancelAnimationFrame(raf);
      raf = 0;
      root.classList.remove('is-phys');
      links.forEach((l) => { l.style.transform = ''; });
    };
    const sync = () => (on('tilt') && !paused() ? wake() : sleep());

    items.forEach((li, i) => {
      const ball = li.querySelector('.kiids-lh__ball');
      li.addEventListener('pointermove', (e) => {
        if (e.pointerType === 'mouse' && on('tilt')) push(i, e.movementX * .7);
      });
      li.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse' || !ball || performance.now() - st[i].sparked < 800) return;
        st[i].sparked = performance.now();
        const b = ball.getBoundingClientRect(), h = li.getBoundingClientRect();
        spark(li, b.left - h.left + b.width / 2, b.top - h.top + b.height / 2);
      });
      li.addEventListener('pointerdown', () => push(i, (Math.random() < .5 ? -1 : 1) * 80));
    });

    const row = root.querySelector('.kiids-lh__items');
    if (row) {
      let x = row.scrollLeft;
      row.addEventListener('scroll', () => {
        const dx = row.scrollLeft - x;
        x = row.scrollLeft;
        st.forEach((s) => { s.v = clamp(s.v - dx * .45, -90, 90); });
        wake();
      }, { passive: true });
    }
    // sky drifts against the pointer (mouse, full motion)
    root.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse' || !on('tilt')) return;
      const r = root.getBoundingClientRect();
      root.style.setProperty('--px', ((e.clientX - r.left) / r.width - .5).toFixed(3));
      root.style.setProperty('--py', ((e.clientY - r.top) / r.height - .5).toFixed(3));
    });
    root.addEventListener('pointerleave', () => { root.style.removeProperty('--px'); root.style.removeProperty('--py'); });
    document.addEventListener('kiids:motion', sync);
    sync();
  };

  // ---- Radosť z jazdy: the vehicle of the chosen age floats on its disc; the mouse tilts it a little ----
  const ride = (root) => {
    const box = root.querySelector('[data-kiids-show]');
    const tilt = box && box.querySelector('.kiids-lh__tilt');
    if (!tilt) return;
    box.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse' || !on('tilt') || paused()) return;
      const r = box.getBoundingClientRect();
      tilt.style.setProperty('--ry', (((e.clientX - r.left) / r.width - .5) * 14).toFixed(1) + 'deg');
      tilt.style.setProperty('--rx', (-((e.clientY - r.top) / r.height - .5) * 10).toFixed(1) + 'deg');
    });
    box.addEventListener('pointerleave', () => { tilt.style.removeProperty('--ry'); tilt.style.removeProperty('--rx'); });
  };

  // ---- Radosť z hry: a ball pit under the board (circle physics on a canvas). Stir it with the mouse, tap to pop more balls in. ----
  const pit = (root) => {
    const cv = root.querySelector('.kiids-lh__cv'), zone = root.querySelector('[data-pit]'), inner = root.querySelector('.kiids-lh__inner');
    if (!cv || !zone || !cv.getContext) return;
    const ctx = cv.getContext('2d');
    const PAL = ['#C6FF3D', '#FF5FA2', '#FFF7E8', '#ebdcec', '#c996cc'];
    let W = 0, H = 0, dpr = 1, area = { l: 0, r: 0, top: 0, floor: 0, bottom: 0 }, balls = [], raf = 0, last = 0, quiet = 0, visible = false, dropped = false, timers = [];
    const mouse = { on: false, x: 0, y: 0, vx: 0, vy: 0, t: 0 };
    const radius = () => (W < 640 ? 12 : 17);
    const cap = () => clamp(Math.round((area.r - area.l) / 15), 18, 56);
    const slot = (i, n) => area.l + 14 + (i + rand(.1, .9)) * ((area.r - area.l - 28) / n); // balls start spread over the pit, so the pile is even

    const size = () => {
      const ir = inner.getBoundingClientRect(), zr = zone.getBoundingClientRect();
      if (!ir.width) return;
      dpr = Math.min(2, window.devicePixelRatio || 1);
      W = ir.width; H = ir.height;
      cv.width = Math.round(W * dpr);
      cv.height = Math.round(H * dpr);
      area = { l: zr.left - ir.left + 5, r: zr.right - ir.left - 5, top: zr.top - ir.top, floor: zr.bottom - ir.top - 18, bottom: zr.bottom - ir.top - 3 };
      balls.forEach((b) => { b.x = clamp(b.x, area.l + b.r, area.r - b.r); b.y = Math.min(b.y, area.floor - b.r); });
    };
    const add = (x, y, vx = rand(-60, 60), vy = rand(0, 120)) => {
      if (balls.length >= cap() + 3) balls.shift(); // the pile stays inside the tray
      balls.push({ x, y, vx, vy, r: radius() * rand(.85, 1.15), c: PAL[Math.floor(Math.random() * PAL.length)] });
    };
    const step = (dt) => {
      const E = .45;
      balls.forEach((b) => {
        b.vy += 2200 * dt;
        b.rest = false;
        b.x += b.vx * dt; b.y += b.vy * dt;
        if (b.x - b.r < area.l) { b.x = area.l + b.r; b.vx = -b.vx * E; }
        if (b.x + b.r > area.r) { b.x = area.r - b.r; b.vx = -b.vx * E; }
        if (b.y + b.r > area.floor) { b.y = area.floor - b.r; b.vy = -b.vy * E; b.vx *= .985; b.rest = true; }
        if (mouse.on) {
          const dx = b.x - mouse.x, dy = b.y - mouse.y, d = Math.hypot(dx, dy), reach = b.r + 36;
          if (d < reach && d > .01) {
            const k = 1 - d / reach;
            b.vx += (dx / d * 220 + mouse.vx * .45) * k;
            b.vy += (dy / d * 220 + mouse.vy * .45) * k;
          }
        }
      });
      for (let it = 0; it < 2; it++) {
        for (let i = 0; i < balls.length; i++) {
          for (let j = i + 1; j < balls.length; j++) {
            const a = balls[i], b = balls[j], dx = b.x - a.x, dy = b.y - a.y, m = a.r + b.r, d2 = dx * dx + dy * dy;
            if (d2 >= m * m || d2 < .0001) continue;
            const d = Math.sqrt(d2), nx = dx / d, ny = dy / d, o = (m - d) / 2;
            a.x -= nx * o; a.y -= ny * o; b.x += nx * o; b.y += ny * o;
            a.rest = b.rest = true;
            const vn = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
            if (vn < 0) { const j2 = -(1 + .35) * vn / 2; a.vx -= j2 * nx; a.vy -= j2 * ny; b.vx += j2 * nx; b.vy += j2 * ny; }
          }
        }
      }
      let moving = false;
      balls.forEach((b) => {
        if (b.rest && b.vx * b.vx + b.vy * b.vy < 200) b.vx = b.vy = 0; else moving = true; // only a ball that touches something may fall asleep (not one at the top of a hop)
      });
      return moving;
    };
    const draw = () => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      ctx.lineWidth = 3;
      balls.forEach((b) => {
        ctx.beginPath(); ctx.arc(b.x, b.y, b.r, 0, 7);
        ctx.fillStyle = b.c; ctx.fill();
        ctx.strokeStyle = '#141414'; ctx.stroke();
        ctx.beginPath(); ctx.arc(b.x - b.r * .2, b.y - b.r * .2, b.r * .5, Math.PI * 1.05, Math.PI * 1.6);
        ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(255,255,255,.75)'; ctx.stroke();
        ctx.lineWidth = 3;
      });
      // front wall of the pit hides the lower part of the balls
      const x = area.l - 2, y = area.floor - 8, w = area.r - area.l + 4, h = area.bottom - y;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(x, y, w, h, [0, 0, 17, 17]); else ctx.rect(x, y, w, h);
      ctx.fillStyle = '#5c02ae'; ctx.fill();
      ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + w, y);
      ctx.strokeStyle = '#141414'; ctx.stroke();
    };
    const run = (t) => {
      if (!visible || document.hidden || paused() || !on('fly')) { raf = 0; return; }
      const dt = Math.min(.033, (t - last) / 1000 || .016);
      last = t;
      let moving = false;
      for (let k = 0; k < 3; k++) moving = step(dt / 3) || moving;
      draw();
      quiet = moving || mouse.on ? 0 : quiet + dt;
      raf = quiet > .5 ? 0 : requestAnimationFrame(run);
    };
    const wake = () => { quiet = 0; if (!raf && on('fly')) { last = performance.now(); raf = requestAnimationFrame(run); } };
    const settle = () => { // static end state: the balls already lie in the pit
      if (!balls.length) for (let i = 0, n = cap(); i < n; i++) add(slot(i, n), rand(area.top, area.floor - 20), 0, 0);
      for (let i = 0; i < 360; i++) step(1 / 60);
      draw();
    };
    const intro = () => { // first view at full motion: the balls tumble into the tray from just above it
      dropped = true;
      balls = [];
      const n = cap();
      const order = [...Array(n).keys()].sort(() => Math.random() - .5);
      order.forEach((slotIx, i) => timers.push(setTimeout(() => { add(slot(slotIx, n), area.top - rand(40, 150), rand(-30, 30), rand(0, 80)); wake(); }, i * 45)));
    };

    size();
    new ResizeObserver(() => { size(); if (!raf) { settle(); } }).observe(inner);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { size(); if (!raf && !dropped) settle(); });
    settle();
    new IntersectionObserver((en) => {
      visible = en[0].isIntersecting;
      if (visible && on('tilt') && !dropped) intro();
      if (visible) wake();
    }, { threshold: .35 }).observe(root);

    const local = (e) => { const r = inner.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
    zone.addEventListener('pointerdown', (e) => {
      if (!on('fly')) return;
      const [x, y] = local(e);
      mouse.on = e.pointerType === 'mouse' && on('tilt');
      for (let i = 0; i < 3; i++) add(x + rand(-14, 14), Math.max(area.top + 10, y - 20), rand(-160, 160), rand(-700, -300));
      balls.forEach((b) => { const dx = b.x - x, dy = b.y - y, d = Math.hypot(dx, dy); if (d < 110 && d > .01) { b.vx += dx / d * 380; b.vy -= 280; } });
      wake();
    });
    zone.addEventListener('pointermove', (e) => {
      if (!on('tilt') || (e.pointerType !== 'mouse' && !e.buttons)) return;
      const [x, y] = local(e), now = performance.now(), dt = Math.max(8, now - mouse.t);
      mouse.vx = clamp((x - mouse.x) / dt * 1000, -900, 900);
      mouse.vy = clamp((y - mouse.y) / dt * 1000, -900, 900);
      mouse.x = x; mouse.y = y; mouse.t = now; mouse.on = true;
      wake();
    });
    zone.addEventListener('pointerleave', () => { mouse.on = false; });
    zone.addEventListener('pointerup', (e) => { if (e.pointerType !== 'mouse') mouse.on = false; });
    // hovering or focusing a window makes the balls below it hop
    root.querySelectorAll('.kiids-lh__item').forEach((li) => {
      const hop = () => {
        if (!on('fly')) return;
        const ir = inner.getBoundingClientRect(), r = li.getBoundingClientRect(), cx = r.left - ir.left + r.width / 2;
        balls.forEach((b) => { if (Math.abs(b.x - cx) < r.width * .6) b.vy -= rand(220, 420); });
        wake();
      };
      li.addEventListener('pointerenter', hop);
      li.addEventListener('focusin', hop);
    });
    document.addEventListener('visibilitychange', () => { if (!document.hidden) wake(); });
    document.addEventListener('kiids:motion', () => { if (on('fly') && !paused()) wake(); });
  };

  // ---- Značka: the striped ring of the badge turns slowly, faster while you hover, and a tap spins it up with a few stars ----
  const brand = (root) => {
    const badge = root.querySelector('.kiids-bhead__badge'), ring = root.querySelector('.kiids-bhead__ring');
    if (!badge || !ring || !ring.animate) return;
    let a = null, rate = 1, target = 1, raf = 0, last = 0;
    const frame = (t) => {
      const dt = Math.min(.05, (t - last) / 1000 || .016);
      last = t;
      rate += (target - rate) * Math.min(1, dt * 3);
      if (Math.abs(rate - target) < .02) rate = target;
      a.playbackRate = rate;
      raf = rate === 1 && target === 1 ? 0 : requestAnimationFrame(frame);
    };
    const to = (v, now) => {
      if (!a) return;
      target = v;
      if (now) rate = now;
      if (!raf) { last = performance.now(); raf = requestAnimationFrame(frame); }
    };
    const build = () => {
      if (a || !on('fly')) return;
      a = ring.animate([{ rotate: '0deg' }, { rotate: '360deg' }], { duration: 48000, iterations: Infinity });
      if (paused()) a.pause();
    };
    badge.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') to(5); });
    badge.addEventListener('pointerleave', () => to(1));
    badge.addEventListener('click', (e) => {
      const r = badge.getBoundingClientRect();
      spark(badge, e.clientX - r.left, e.clientY - r.top);
      to(1, 18);
    });
    document.addEventListener('kiids:motion', () => {
      if (!on('fly')) { if (a) { a.cancel(); a = null; } return; }
      build();
      if (a) { if (paused()) a.pause(); else a.play(); }
    });
    build();
  };

  const init = (scope) => {
    scope.querySelectorAll('[data-kiids-lh]:not([data-ready])').forEach((root) => {
      root.setAttribute('data-ready', '');
      const kind = root.dataset.kiidsLh;
      if (kind === 'mobile') crib(root); else if (kind === 'track') ride(root); else if (kind === 'board') pit(root); else if (kind === 'brand') brand(root);
    });
  };
  init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
